import styled from '@emotion/styled';
import { BucketId, FileReference } from '@kaizenplatform/belfast-entity';
import { Chip as _Chip, Divider, Typography } from '@material-ui/core';
import _ from 'lodash';
import { createElement, FC, Fragment, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  BusinessRegion,
  CreationSetForProjectFragment,
  CreationState,
  Creative,
  Deliverable,
  File,
  GetCreationByIdQuery,
  Issue,
  IssueComment,
  Maybe,
  ModificationRequestDeliverable,
  OfferPricingDetailItem,
  Organization,
  ProjectType as ProjectTypeType,
  Request,
  RequestItem,
  RequestState,
  User,
  UserDirector,
} from '../../../graphql/generated/graphql';
import { SingleOfferFragment } from '../../../graphql/generated/graphql_offer';
import { ProjectType } from '../../../service/persistent/ProjectWebApi';
import getAccessingDevice from '../../../utility/getAccessingDevice';
import _InspectionStatus from '../../component/compound/CreationDetail/InspectionStatus';
import ModificationRequestUnSupportedDeviceCard from '../../component/compound/CreationDetail/ModificationRequest/ModificationRequestUnSupportedDeviceCard';
import _ModificationStatusIcon from '../../component/compound/CreationDetail/ModificationStatusIcon';
import _SubmissionStatusIcon from '../../component/compound/CreationDetail/SubmissionStatusIcon';
import CreationInspectionWorkflow from '../../component/compound/CreationInspectionWorkflow';
import _DeliveryDueDate from '../../component/compound/DeliveryDueDate';
import ExpansionPanel from '../../component/compound/ExpansionPanel';
import _ProjectSummary from '../../component/compound/ProjectSummary';
import Spinner from '../../component/compound/Spinner';
import LfToBr from '../../component/primitive/LfToBr';
import _Breadcrumbs from '../../connected/Breadcrumbs';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import CreationSummary from './CreationSummary';
import ModificationContent from './ModificationContent';
import ModificationDescription from './ModificationDescription';
import _SubmissionContent from './SubmissionContent';

interface Props {
  project: Pick<
    ProjectType,
    | 'id'
    | 'hasChanges'
    | 'title'
    | 'matchingFormat'
    | 'designInspirationCase'
    | 'assetCategoryValue'
    | 'assetCategoryDefinition'
    | 'creativeBriefDefinition'
    | 'creativeBriefValue'
  > & {
    organization: Pick<Organization, 'name'> & {
      businessRegion: Pick<BusinessRegion, 'key'>;
    };
    projectType: Pick<ProjectTypeType, 'thumbnailImageUrl' | 'key' | 'sampleVideoUrl' | 'title' | 'description'>;
  };
  paramSelectedModificationRequestId: string;
  creation: Pick<
    NonNullable<GetCreationByIdQuery['creation']>,
    | 'creativeFormat'
    | 'assignmentId'
    | 'deliverables'
    | 'state'
    | 'proposed'
    | 'assignee'
    | 'inspectionModificationRequests'
    | 'achievementId'
  > & {
    deliverables: Array<Pick<Deliverable, 'id' | 'createdAt'>>;
    modificationRequests: Array<
      Pick<Request, 'state' | 'targetDeliverableId'> & {
        deliverables: Array<Pick<ModificationRequestDeliverable, 'id'>>;
      }
    >;
  };
  assetFiles: ReadonlyArray<FileReference>;
  briefFiles: Map<BucketId, FileReference[]>;
  loading: boolean;
  modificationRequestAssets: Array<FileReference>;
  hideSubmissionForm: boolean;
  panelExpanded: {
    creativeBrief: boolean;
    submission: boolean;
    modification: boolean;
  };
  targetCreationSet: Pick<CreationSetForProjectFragment, 'expressDueDays' | 'creativeFormats'>;
  offer: Pick<SingleOfferFragment, 'expectedDue'>;
  modificationRequests: (Pick<
    Request,
    'id' | 'state' | 'deliveryDue' | 'submittedAt' | 'feeType' | 'targetDeliverableId' | 'bucketId' | 'touchedAt'
  > & {
    sender: Pick<UserDirector, 'profileImageUrl' | 'name'>;
    deliverables: (Pick<Deliverable, 'intent' | 'createdAt'> & {
      creative: Pick<Creative, 'id'> & {
        file: Pick<File, 'url' | 'mimeType' | 'fastStart' | 'attribute' | 'originalFileName'>;
      };
      attachedThumbnail?: Maybe<{
        file: Pick<File, 'url' | 'size' | 'originalFileName'>;
      }>;
    })[];
    offerPricingDetail?: Maybe<{
      basicPricings: Array<Pick<OfferPricingDetailItem, 'rewardAmount' | 'rewardCurrency'>>;
      optionPricings: Array<Pick<OfferPricingDetailItem, 'rewardAmount' | 'rewardCurrency'>>;
    }>;
    items: (Pick<RequestItem, 'id' | 'typeName' | 'metadata' | 'sender' | 'assetIds' | 'body'> & {
      issue?: Maybe<
        Pick<Issue, 'id' | 'resolved'> & {
          comments: (Pick<IssueComment, 'assetIds' | 'body' | 'createdAt'> & {
            user: Pick<User, 'name' | 'profileImageUrl'>;
          })[];
        }
      >;
    })[];
  })[];
  refresh: () => void;
  className?: string;
  language: string;
}

const CreationContent: FC<Props> = ({
  project,
  paramSelectedModificationRequestId,
  creation,
  assetFiles,
  briefFiles,
  loading,
  modificationRequestAssets,
  hideSubmissionForm,
  panelExpanded,
  targetCreationSet,
  offer,
  modificationRequests,
  refresh,
  className,
  language,
}) => {
  const [selectedModificationRequestId, onSelectedModificationRequestId] = useState<string>(
    paramSelectedModificationRequestId || (creation.deliverables.length > 0 && modificationRequests.length > 0)
      ? modificationRequests[0].id
      : '',
  );

  const selectedModificationRequest = modificationRequests.find(
    (modificationRequest) => modificationRequest.id === selectedModificationRequestId,
  );
  const resizeBaseCreativeFormat = creation.creativeFormat.resizeBaseId
    ? targetCreationSet.creativeFormats.find(
        (creativeFormat) => creativeFormat.id == creation.creativeFormat.resizeBaseId,
      )
    : null;
  const resizeCreativeFormats = targetCreationSet.creativeFormats.filter(
    (creativeFormat) => creativeFormat.resizeBaseId === creation.creativeFormat.id,
  );

  return (
    <Root className={className}>
      <Breadcrumbs />

      <ProjectSummary
        project={project}
        creationState={creation.state}
        offer={offer}
        targetCreationSet={targetCreationSet}
        passedDeadline={false}
        creativeCount={creation.deliverables.length}
        creativeFormat={creation.creativeFormat}
        baseCreativeFormat={resizeBaseCreativeFormat ? resizeBaseCreativeFormat : null}
        resizeCreativeFormats={resizeCreativeFormats}
      />

      <CreationDetails>
        {/*  1. 制作指示書 */}
        <ExpansionPanel
          summary={<FormattedMessage id="show_creation_page.section_heading.creative_brief" />}
          defaultExpanded={panelExpanded.creativeBrief}
        >
          <CreationSummary
            project={project}
            hasProjectChange={project.hasChanges}
            creativeFormat={creation.creativeFormat}
            assetFiles={assetFiles}
            briefFiles={briefFiles}
          />
        </ExpansionPanel>
        {/*  2. 納品フォーム */}
        <ExpansionPanel
          summary={
            <SectionSummary>
              <FormattedMessage id="show_creation_page.section_heading.submit_creative" />
              <SubmissionStatusIcon
                done={
                  selectedModificationRequest
                    ? selectedModificationRequest.state === RequestState.Approved
                    : creation.state === CreationState.Submitted
                }
                isCanceled={creation.state === CreationState.Canceled}
              />
              <DeliveryDueDate dueDate={offer.expectedDue} />
              {targetCreationSet?.expressDueDays ? (
                <Chip label={<FormattedMessage id="show_creation_page.express" />} />
              ) : null}
            </SectionSummary>
          }
          defaultExpanded={panelExpanded.submission}
        >
          <SubmissionDescription>
            {language === 'ja-JP' && <CreationInspectionWorkflow />}
            <Typography>
              <FormattedMessage id="show_creation_page.description.submission_inspection_announce">
                {(text: string) => <LfToBr text={text} />}
              </FormattedMessage>
            </Typography>
          </SubmissionDescription>
          <SubSectionDivider />
          {!loading ? (
            <SubmissionContent
              assignmentId={creation.assignmentId}
              creation={creation}
              hideForm={hideSubmissionForm}
              refresh={refresh}
            />
          ) : (
            <Spinner />
          )}
          {creation.state !== CreationState.Canceled ? (
            <Fragment>
              <Divider />
              <InspectionStatus state={creation.state} proposed={creation.proposed} />
            </Fragment>
          ) : null}
        </ExpansionPanel>
        {/*  3. 修正依頼フォーム */}
        <ExpansionPanel
          summary={
            <SectionSummary>
              <FormattedMessage id="show_creation_page.section_heading.modification" />
              <ModificationStatusIcon
                done={creation.state === CreationState.Submitted}
                exists={modificationRequests.length > 0}
                isCanceled={creation.state === CreationState.Canceled}
              />
              {selectedModificationRequest &&
              selectedModificationRequest.deliveryDue &&
              creation.state !== CreationState.Submitted ? (
                <DeliveryDueDate dueDate={selectedModificationRequest.deliveryDue} />
              ) : null}
            </SectionSummary>
          }
          defaultExpanded={panelExpanded.modification}
        >
          {getAccessingDevice() === 'sp' ? (
            <ModificationRequestUnSupportedDeviceCard />
          ) : (
            <Fragment>
              <ModificationDescription hasModificationRequest={modificationRequests.length > 0} />

              {creation.deliverables.length > 0 && modificationRequests.length > 0 ? (
                !loading ? (
                  <ModificationContent
                    creation={creation}
                    assets={modificationRequestAssets}
                    modificationRequests={modificationRequests}
                    selectedModificationRequestId={selectedModificationRequestId}
                    onSelectedModificationRequestId={onSelectedModificationRequestId}
                    selectedModificationRequest={selectedModificationRequest}
                    hideSubmissionForm={hideSubmissionForm}
                    refresh={refresh}
                  />
                ) : (
                  <Spinner />
                )
              ) : null}
            </Fragment>
          )}
        </ExpansionPanel>
      </CreationDetails>
    </Root>
  );
};

const Root = styled('div')({
  margin: `0 ${SPACING_UNIT}px`,
  padding: `${SPACING_UNIT * 5}px calc((100% - 960px) / 2) ${SPACING_UNIT * 10}px`,
});

const Breadcrumbs = styled(_Breadcrumbs)({ marginBottom: SPACING_UNIT * 5 });

const ProjectSummary = styled(_ProjectSummary)({ marginBottom: SPACING_UNIT * 5 });

const CreationDetails = styled('div')({ marginTop: SPACING_UNIT * 3 });

const SectionSummary = styled('div')(
  flexibleLayoutContainer({
    alignItems: 'center',
  }),
);

const SubmissionStatusIcon = styled(_SubmissionStatusIcon)({
  paddingLeft: SPACING_UNIT / 2,
});

const ModificationStatusIcon = styled(_ModificationStatusIcon)({
  paddingLeft: SPACING_UNIT / 2,
});

const DeliveryDueDate = styled(_DeliveryDueDate)({
  marginLeft: SPACING_REGULAR,
  fontWeight: 550,
  border: '1px solid',
  borderRadius: '15px',
  padding: '4px 12px',
});

const Chip = styled(_Chip)({
  marginLeft: SPACING_REGULAR / 2,
});

const SubSectionDivider = styled(Divider)({ marginLeft: -SPACING_UNIT * 3, marginRight: -SPACING_UNIT * 3 });

const SubmissionDescription = styled('div')({ margin: `0 0 ${SPACING_UNIT * 3}px` });

const SubmissionContent = styled(_SubmissionContent)({ margin: `${SPACING_UNIT * 3}px 0` });

const InspectionStatus = styled(_InspectionStatus)({ margin: `${SPACING_UNIT * 2}px 0 0` });

export default CreationContent;
